import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';

const navLinks = [
  { label: 'Products', href: '/products' },
  { label: 'Tech Wiki', href: '/tech-wiki' },
  { label: 'About', href: '/about' },
  { label: 'Contact', href: '/contact' },
];

const DARK_ENTRY_ROUTES = new Set(['/dealer']);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { theme } = useTheme();
  const light = theme === 'light';
  const needsLightLogo = !light || DARK_ENTRY_ROUTES.has(location.pathname);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const scrolledBg = light ? 'bg-white border-b border-gray-200' : 'bg-[#0D1B2A]';
  const linkColor = light
    ? 'text-gray-600 hover:text-gray-900'
    : 'text-white/80 hover:text-white';
  const mobileBg = light ? 'bg-white border-t border-gray-200' : 'bg-[#0D1B2A] border-t border-white/10';
  const hamburgerColor = light ? 'text-gray-800' : 'text-white';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? scrolledBg : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center cursor-pointer">
          <img
            src="https://static.readdy.ai/image/3bab8804b943b0f9d2e99ce4e4e8123d/a7b2a29c0c21ce8564d3744200077816.png"
            alt="Stingray"
            className={`h-10 w-auto object-contain brightness-0 ${needsLightLogo ? 'invert' : ''}`}
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`text-sm font-medium tracking-wide transition-colors cursor-pointer whitespace-nowrap ${
                location.pathname === link.href ? 'text-[var(--accent)]' : linkColor
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Mobile Hamburger */}
        <button
          className={`md:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${hamburgerColor}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${menuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className={`md:hidden px-6 py-4 flex flex-col gap-4 ${mobileBg}`}>
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`text-sm font-medium py-1 cursor-pointer ${light ? 'text-gray-700 hover:text-gray-900' : 'text-white/80 hover:text-white'}`}
            >
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
}
